import { useCallback, useEffect, useState } from 'react';
import { useLearningSessionStatus } from '@/providers/learning-session-context';
import { useLearningSessionTrackerEnabled } from '@/hooks/useLearningSessionTracker';
import { formatDurationCompact } from '@/lib/format-duration';
import { LearningSessionStatusCard } from './LearningSessionStatusCard';

const STORAGE_KEY = 'lms-learning-session-dock-open';

function readStoredOpen(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function LearningSessionStatusDock() {
  const enabled = useLearningSessionTrackerEnabled();
  const status = useLearningSessionStatus();
  const [open, setOpen] = useState(readStoredOpen);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        setOpen(false);
        try {
          localStorage.setItem(STORAGE_KEY, '0');
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!enabled || !status?.config) {
    return null;
  }

  const sessionUsed = Math.min(status.currentSessionSeconds, status.config.maxSessionSeconds);
  const sessionLabel = formatDurationCompact(sessionUsed);

  return (
    <div className={`lms-learning-session-dock${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="lms-learning-session-dock__tab"
        onClick={toggle}
        aria-expanded={open}
        aria-controls="learning-session-dock-panel"
        title={open ? 'Ocultar tiempo de sesión' : 'Mostrar tiempo de sesión'}
      >
        <span className="material-symbols-outlined lms-learning-session-dock__tab-icon" aria-hidden="true">
          timer
        </span>
        <span className="lms-learning-session-dock__tab-time">{sessionLabel}</span>
        <span className="material-symbols-outlined lms-learning-session-dock__tab-chevron" aria-hidden="true">
          {open ? 'chevron_right' : 'chevron_left'}
        </span>
      </button>

      <div
        id="learning-session-dock-panel"
        className="lms-learning-session-dock__panel"
        aria-hidden={!open}
      >
        <LearningSessionStatusCard />
      </div>
    </div>
  );
}
